import { services } from '@repo/ui/lib/api/services/index';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DroitCreateInput, DroitFilters, DroitListResponse, DroitResponse, DroitSelectOption, DroitUpdateInput } from '../../../lib/api/types/droit';
import { useQueryWithConfig } from '../useQueryWithConfig';

// Hooks for fetching droits
export const useDroits = (filters: DroitFilters = {}) => {
  const { page = 1, limit = 10, sortBy, sortOrder, ...filterParams } = filters;
  const keys = ["droits", "list", page, limit, sortBy, sortOrder, JSON.stringify(filterParams)].filter(Boolean);
  
  return useQueryWithConfig<DroitListResponse>(
    keys as string[],
    () => services.droit.getDroits(filters)
  );
};

export const useDroit = (id: number) => {
  return useQueryWithConfig<DroitResponse>(
    ["droits", "detail", id],
    () => services.droit.getDroitById(id),
    {
      enabled: !!id,
    }
  );
};

export const useDroitOptions = () => {
  return useQueryWithConfig<DroitSelectOption[]>(
    ["droits", "options"],
    () => services.droit.getDroitOptions()
  );
};

// Hooks for droit mutations
export const useCreateDroit = () => {
  const queryClient = useQueryClient();
  
  return useMutation<DroitResponse, Error, DroitCreateInput>({
    mutationFn: (data) => services.droit.createDroit(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["droits", "list"] });
      queryClient.invalidateQueries({ queryKey: ["droits", "options"] });
    },
  });
};

export const useUpdateDroit = () => {
  const queryClient = useQueryClient();
  
  return useMutation<DroitResponse, Error, { id: number; data: DroitUpdateInput }>({
    mutationFn: ({ id, data }) => services.droit.updateDroit(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["droits", "detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["droits", "list"] });
      queryClient.invalidateQueries({ queryKey: ["droits", "options"] });
    },
  });
};

export const useDeleteDroit = () => {
  const queryClient = useQueryClient();
  
  return useMutation<DroitResponse, Error, number>({
    mutationFn: (id) => services.droit.deleteDroit(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["droits", "detail", id] });
      queryClient.invalidateQueries({ queryKey: ["droits", "list"] });
      queryClient.invalidateQueries({ queryKey: ["droits", "options"] });
    },
  });
};
